import { useAuth, API_ORIGIN, authorizedFetch } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'
import type { Opportunity, PaginatedOpportunities, OpportunitiesFilters } from './use-opportunities'

export function useMarketplaceOpportunities(filters: OpportunitiesFilters = {}) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['marketplace-opportunities', filters],
    queryFn: async (): Promise<PaginatedOpportunities> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (filters.q) params.set('q', filters.q)
      if (filters.location) params.set('location', filters.location)
      if (filters.mode) params.set('mode', filters.mode)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.page_size) params.set('page_size', String(filters.page_size))
      const qs = params.toString()
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/marketplace/${qs ? `?${qs}` : ''}`,
      )
      if (!response.ok) throw new Error('Failed to fetch marketplace opportunities')
      const data = await response.json()
      return {
        ...data,
        results: (data.results || []).map((row: Opportunity) => ({ ...row, kind: row.kind ?? 'marketplace' })),
      }
    },
    enabled: !!token,
  })
}

export function useMarketplaceOpportunity(opportunityId: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['marketplace-opportunity', opportunityId],
    queryFn: async () => {
      if (!token || !opportunityId) throw new Error('Missing id')
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/marketplace/${encodeURIComponent(opportunityId)}`,
      )
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Not found')
      return data as Opportunity & {
        redirect_url?: string
        attribution_label?: string
        attribution_url?: string
        marketplace_status?: string
      }
    },
    enabled: !!token && !!opportunityId,
  })
}
